import * as THREE from 'three';
import { Visualizer } from '../visualizer.js';
import { safeZoneUniforms } from '../shaders/safeZone.glsl.js';
import { coverScale } from '../artwork-texture.js';
import { getBalloons } from '../balloons-assets.js';
import { VDJ_PRESETS } from './lib/vdjLook.js';

// Balloons - a rocky valley at dawn, hot-air balloons rising slowly out of
// it. Their burners light on the bar and make them glow like lanterns, while
// the day breaks over the track. Downtempo, indie folk, soft pop, rhythmic
// ambient, chill. Rules of kiosk-app/CLAUDE.md (R1-R17); danceability 0.46 =
// R9's lounge band (VDJ_PRESETS.lounge): no flash, no hit.
//
// THE PHOTO (tools/build-balloons.mjs: rocky-valley.jpeg, its sky mask, the
// scene's PROXIMITY from the depth network, the measured sun) is one quad,
// graded from blue dawn to golden morning over progress 0..1: exposure and
// warmth in the sky and on the lit rocks (brighter faces more), and the sun's
// glow rising on the horizon. The grading is a pure function of a smoothed
// progress: no step.
//
// THE BALLOONS: one InstancedMesh of 12-18 quads (R12: each quad 6 halo
// widths larger than the envelope, so the glow reaches 0 inside it). Nothing
// is uploaded per frame but uniforms: per balloon a vec4 (x, y, size, its
// proximity) and a vec4 (burn, flame, backlight, haze), its two gore colours.
// Each fragment draws the envelope by DISTANCE (a pear: a sphere over a
// curved cone to the mouth), vertical gores of alternating colours, a basket,
// four 1 px cables (R1), soft dawn shading from the sun's side, a light
// backlight (R13: the body goes dark, only the sun-facing rim lights) near
// the sun, aerial haze for the far ones. OCCLUSION: a balloon has ONE
// proximity (its size: bigger = nearer); it is hidden wherever the scene's
// proximity (the depth map at that pixel) is greater - the far ones vanish
// behind the hills and rock spires, the near ones only behind the
// foreground.
//
// THE BURNER - the decisive detail. When it fires the envelope lights FROM
// INSIDE: the fabric turns translucent and glowing, the bottom more than the
// top, the seams staying dark like the ribs of a lantern; a yellow-orange
// flame shows in the mouth; a warm glow around. 0.15 beat up, held to 1.5
// beats, out over one bar. The balloon climbs a little faster afterwards.
// Burners are brighter in the half-dark of dawn than in the morning.
//
// RHYTHM (lounge): bar: one burner, on a different balloon each time;
// phrase: two together; drop: every burner in a cascade over 2 bars.
//
// FRAMING (R6, criterion 4): balloons are born outside the DOM cover's band
// and the UI zones, and steer sideways (a look-ahead of LOOKAHEAD_S along
// their climb, in time) around the cover's zone and the three UI zones: none stays in
// one for more than a moment.

const RIG = 'forbidden';
export { RIG };

export const PROFILE = {
  id: 'balloons',
  family: 'photographic',
  danceability: 0.46,
  requiresTier: 0,
  energyLevel: 0.35,
  bpmMin: 70,
  bpmMax: 120,
  movement: 0.30,
  tonality: 0.85,
  enabled: true
};

const LOUNGE = VDJ_PRESETS.lounge;
const MAX_BALLOONS = 18;
const TIER = { 0: { count: 12 }, 1: { count: 15 }, 2: { count: 18 } };
const DEFAULT_TIER = 2;

// --- the balloons (screen height units: the frame is 2 tall) ---------------
const SIZE_MIN = 0.04, SIZE_MAX = 0.28;   // envelope height: 2 % .. 14 % of the screen
const PROX_PER_SIZE = 0.80;               // proximity = size x this (0.03 .. 0.22: between the far valley and the foreground rocks)
const NEAR_SPEED = 2 / 150;               // a near balloon crosses the screen in 2.5 min
const WIND = 0.006;                       // lateral drift of a near balloon, units/s
const BOOST = 0.6;                        // climb x (1 + BOOST) just after a burn
const BOOST_BEATS = 8;
const STEER_SPEED = 0.035;                // units/s, whatever the size (a slow far balloon must still get out of the way)
const QUAD_X = 0.95, QUAD_Y = 1.15;       // the quad's half size, x the envelope height
// --- the burn (beats) ---------------------------------------------------------
const BURN_ATTACK = 0.15, BURN_HOLD = 1.5, BURN_DECAY = 4;
const GLOW_DAWN = 1.0, GLOW_MORNING = 0.45;
const DEFAULT_PAIRS = [['#a8301c', '#c89a3a'], ['#24548a', '#c8c2b0'], ['#5e2f7a', '#c4801c'], ['#226a46', '#c8b890'], ['#922448', '#2c3c6f']];
const SAFE_FLOOR = 0.6;
const PROGRESS_TAU = 2.0;
const RADIO_LOOP_SECONDS = 600;
const FREE_BPM = 92;

function urlParam(name) {
  if (typeof window === 'undefined' || !window.location) return null;
  try { return new URLSearchParams(window.location.search).get(name); } catch (e) { return null; }
}
function clamp01(x) { return Math.max(0, Math.min(1, x)); }
function smooth01(x) { const t = clamp01(x); return t * t * (3 - 2 * t); }
function rand(a, b) { return a + Math.random() * (b - a); }

// ---------------------------------------------------------------------------
// The photo: dawn to morning
// ---------------------------------------------------------------------------
const quadVertex = `
  varying vec2 vUv;
  void main() { vUv = uv; gl_Position = vec4(position.xy, 0.9999, 1.0); }
`;
const photoFragment = `
  precision mediump float;
  uniform sampler2D uPhoto;
  uniform sampler2D uMask;
  uniform vec2 uFit;
  uniform float uP;              // progress, smoothed
  uniform vec2 uSun;             // screen units
  uniform float uAspect;
  varying vec2 vUv;
  void main() {
    vec2 puv = (vUv - 0.5) * uFit + 0.5;
    vec3 photo = texture2D(uPhoto, puv).rgb;
    vec2 m = texture2D(uMask, puv).rg;            // r sky, g proximity
    float sky = m.r, prox = m.g;
    float p = uP;
    vec2 s = (vUv * 2.0 - 1.0) * vec2(uAspect, 1.0);
    float l = dot(photo, vec3(0.2126, 0.7152, 0.0722));
    // the sky: brighter and warmer toward morning
    vec3 skyCol = photo * mix(vec3(1.0), vec3(1.40, 1.22, 0.96), p);
    // the land: the lit faces (brighter in the photo) warm up most
    float lit = smoothstep(0.03, 0.35, l);
    vec3 land = photo * mix(vec3(1.0), vec3(1.55, 1.28, 0.92) * (0.75 + 0.60 * lit), p);
    vec3 col = mix(land, skyCol, sky);
    // the sun's glow, rising on the horizon; a golden haze over the far land
    float d = length(s - uSun);
    vec3 sunCol = mix(vec3(1.0, 0.55, 0.25), vec3(1.0, 0.82, 0.55), p);
    float glow = (exp(-d / 0.28) * 0.10 + exp(-d / 0.06) * 0.30) * (0.25 + 0.75 * p);   // first try 0.30 / 0.35 whited half the frame
    col += sunCol * glow * (sky + (1.0 - sky) * 0.10 * (1.0 - smoothstep(0.0, 0.2, prox)));
    gl_FragColor = vec4(col, 1.0);
  }
`;

// ---------------------------------------------------------------------------
// The balloons
// ---------------------------------------------------------------------------
const balloonVertex = `
  precision highp float;
  attribute float aIndex;
  uniform vec4 uB[${MAX_BALLOONS}];       // x, y, size, proximity
  uniform vec4 uC[${MAX_BALLOONS}];       // burn, flame flicker, backlight, haze
  uniform vec3 uColA[${MAX_BALLOONS}];
  uniform vec3 uColB[${MAX_BALLOONS}];
  uniform float uAspect;
  varying vec2 vQ;                         // envelope-height units, centred on the envelope
  varying vec4 vB;
  varying vec4 vC;
  varying vec3 vA;
  varying vec3 vBc;
  varying vec2 vS;                         // screen units
  void main() {
    int i = int(aIndex + 0.5);
    vec4 b = uB[i];
    vB = b; vC = uC[i]; vA = uColA[i]; vBc = uColB[i];
    vQ = position.xy * vec2(${QUAD_X.toFixed(2)}, ${QUAD_Y.toFixed(2)});
    vec2 s = b.xy + vQ * b.z;
    vS = s;
    gl_Position = vec4(s.x / uAspect, s.y, 0.0, 1.0);
  }
`;

const balloonFragment = `
  precision highp float;
  uniform sampler2D uMask;
  uniform vec2 uFit;
  uniform float uAspect;
  uniform vec2 uSun;
  uniform vec3 uSunCol;
  uniform float uSunPow;         // the dawn light's strength (progress)
  uniform vec3 uAmbient;
  uniform vec3 uHaze;
  uniform float uGlowGain;
  uniform float uNoOcc;          // ?balloonsnoocc=1 (tests)
  uniform float uPxUnit;
  uniform vec2 uSafeCenter;
  uniform vec2 uSafeHalfSize;
  uniform float uSafeFeather;
  varying vec2 vQ;
  varying vec4 vB;
  varying vec4 vC;
  varying vec3 vA;
  varying vec3 vBc;
  varying vec2 vS;

  // half width of the envelope at height y (envelope-height units; the
  // sphere's centre at 0.20, radius 0.42; the mouth at -0.38, half width 0.11)
  float halfW(float y) {
    if (y >= 0.20) return sqrt(max(0.0, 0.1764 - (y - 0.20) * (y - 0.20)));
    float t = clamp((y + 0.38) / 0.58, 0.0, 1.0);
    return 0.11 + 0.31 * pow(sin(1.5708 * t), 0.85);
  }
  float segD(vec2 p, vec2 a, vec2 b) { vec2 pa = p - a, ba = b - a; float h = clamp(dot(pa, ba) / dot(ba, ba), 0.0, 1.0); return length(pa - ba * h); }
  float safeAtt(vec2 s) {
    vec2 q = abs(s - uSafeCenter) - uSafeHalfSize;
    float d = length(max(q, 0.0)) + min(max(q.x, q.y), 0.0);
    return mix(${SAFE_FLOOR.toFixed(2)}, 1.0, smoothstep(0.0, uSafeFeather, d));
  }

  void main() {
    vec2 q = vQ;
    float S = vB.z;
    float px = 1.0 / (uPxUnit * S);           // one screen pixel in envelope units
    float burn = vC.x;
    // --- occlusion: hidden where the scene is nearer ---
    vec2 vuv = vec2(vS.x / uAspect, vS.y) * 0.5 + 0.5;
    vec2 puv = (vuv - 0.5) * uFit + 0.5;
    float sceneProx = texture2D(uMask, puv).g;
    float vis = uNoOcc > 0.5 ? 1.0 : smoothstep(sceneProx - 0.008, sceneProx + 0.004, vB.w);
    if (vis <= 0.0) discard;
    // --- the envelope ---
    float y = q.y;
    float w = halfW(y);
    float inY = step(-0.38, y) * step(y, 0.62);
    // signed distance (approx.): horizontal to the side, radial on the cap
    float dEnv = y >= 0.20 ? length(q - vec2(0.0, 0.20)) - 0.42 : max(abs(q.x) - w, -0.38 - y);
    if (y < -0.38) dEnv = max(dEnv, -0.38 - y);
    float cov = 1.0 - smoothstep(-px, px, dEnv);
    vec3 col = vec3(0.0);
    float alpha = 0.0;
    vec2 L2 = normalize(uSun - vB.xy + vec2(1e-4));
    vec3 L = normalize(vec3(L2, 0.35));
    if (cov > 0.0) {
      float u = clamp(q.x / max(w, 1e-3), -1.0, 1.0);
      float ang = asin(u);
      float g = (ang / 3.14159 + 0.5) * 8.0;            // 8 gores across the visible half
      float gi = floor(g);
      vec3 gore = mod(gi, 2.0) < 0.5 ? vA : vBc;
      // the seams between gores: 1 px lines by their distance in pixels (R1)
      float seam = 1.0 - smoothstep(0.5, 1.5, min(fract(g), 1.0 - fract(g)) / max(fwidth(g), 1e-4));
      // the normal: across the gores, and up/down the cap and the cone
      float ny = y >= 0.20 ? (y - 0.20) / 0.42 : -0.35 * (1.0 - (y + 0.38) / 0.58);
      vec3 n = normalize(vec3(u, ny, sqrt(max(0.0, 1.0 - u * u))));
      float dif = max(0.0, dot(n, L));
      vec3 lit = gore * (uAmbient + uSunCol * dif * uSunPow);
      // backlight (R13): the body darkens, the rim facing the sun lights
      float bl = vC.z;
      float rimD = -dEnv / px;                           // px inside the edge
      float rim = (1.0 - smoothstep(1.5, 3.0, rimD)) * max(0.0, dot(normalize(vec2(q.x, y - 0.1)), L2));
      lit = mix(lit, vec3(0.0), bl * 0.85) + uSunCol * rim * bl * 0.9;
      // the burner: the fabric glows from inside, the bottom most, the seams dark
      // translucent fabric: the gore's own colour, lifted toward the flame's,
      // brightest at the mouth and fading to the crown
      vec3 warm = mix(gore * 2.6, vec3(1.0, 0.70, 0.36), 0.40);
      float inner = (0.30 + 0.70 * smoothstep(0.62, -0.38, y)) * burn * uGlowGain;
      lit = mix(lit, lit * 0.4, inner) + warm * inner * (1.0 - 0.8 * seam) * 1.35;
      lit *= 1.0 - 0.55 * seam * (1.0 - burn * 0.3);
      // aerial haze for the far ones
      lit = mix(lit, uHaze, vC.w);
      col = lit; alpha = cov;
    }
    // --- the basket, the cables, the flame (below the mouth) ---
    vec2 bq = q - vec2(0.0, -0.50);
    float basket = 1.0 - smoothstep(-px, px, max(abs(bq.x) - 0.055, abs(bq.y) - 0.035));
    if (basket > 0.0) { vec3 bc = vec3(0.16, 0.10, 0.06) * (uAmbient * 2.0 + uSunCol * 0.4 * uSunPow); bc = mix(bc, uHaze, vC.w); col = mix(col, bc, basket * (1.0 - alpha)); alpha = max(alpha, basket); }
    float cab = min(segD(q, vec2(-0.105, -0.38), vec2(-0.05, -0.465)), segD(q, vec2(0.105, -0.38), vec2(0.05, -0.465)));
    cab = min(cab, min(segD(q, vec2(-0.04, -0.38), vec2(-0.02, -0.465)), segD(q, vec2(0.04, -0.38), vec2(0.02, -0.465))));
    float cable = (1.0 - smoothstep(0.5, 1.2, cab / px)) * clamp(1.5 * px / max(px, 0.002), 0.0, 1.0) * 0.8;
    if (cable > 0.0 && alpha < 0.99) { vec3 cc = mix(vec3(0.05), uHaze, vC.w); col = mix(col, cc, cable * (1.0 - alpha)); alpha = max(alpha, cable); }
    // the flame in the mouth, and the warm glow around the envelope (R12: 0 well inside the quad)
    vec3 add = vec3(0.0);
    if (burn > 0.01) {
      vec2 fq = (q - vec2(0.0, -0.40)) / vec2(0.035, 0.07);
      float flame = exp(-dot(fq, fq) * 1.4) * burn * vC.y;
      add += vec3(1.0, 0.70, 0.25) * flame * 1.1;
      float glow = exp(-max(dEnv, 0.0) / 0.07) * (1.0 - cov) * burn * uGlowGain * 0.22;
      add += vec3(1.0, 0.62, 0.30) * glow;
    }
    float a = alpha * vis;
    vec3 outc = (col * alpha + add) * vis * safeAtt(vS);
    gl_FragColor = vec4(outc, a);               // premultiplied
  }
`;

let MATS = null;
function materials() {
  if (MATS) return MATS;
  const shared = { uMask: { value: null }, uFit: { value: new THREE.Vector2(1, 1) }, uAspect: safeZoneUniforms.uAspect };
  const pu = Object.assign({ uPhoto: { value: null }, uP: { value: 0 }, uSun: { value: new THREE.Vector2(1, 0.15) } }, shared);
  const arr4 = () => Array.from({ length: MAX_BALLOONS }, () => new THREE.Vector4());
  const arr3 = () => Array.from({ length: MAX_BALLOONS }, () => new THREE.Color());
  const bu = Object.assign({
    uB: { value: arr4() }, uC: { value: arr4() }, uColA: { value: arr3() }, uColB: { value: arr3() },
    uSun: pu.uSun, uSunCol: { value: new THREE.Color(1, 0.7, 0.45) }, uSunPow: { value: 0.3 },
    uAmbient: { value: new THREE.Color(0.10, 0.12, 0.18) }, uHaze: { value: new THREE.Color(0.25, 0.28, 0.38) },
    uGlowGain: { value: 1 }, uNoOcc: { value: 0 }, uPxUnit: { value: 360 },
    uSafeCenter: safeZoneUniforms.uSafeCenter, uSafeHalfSize: safeZoneUniforms.uSafeHalfSize, uSafeFeather: safeZoneUniforms.uSafeFeather
  }, shared);
  const photo = new THREE.ShaderMaterial({ uniforms: pu, vertexShader: quadVertex, fragmentShader: photoFragment, depthTest: false, depthWrite: false });
  const balloon = new THREE.ShaderMaterial({
    uniforms: bu, vertexShader: balloonVertex, fragmentShader: balloonFragment,
    transparent: true, depthTest: false, depthWrite: false,
    blending: THREE.CustomBlending, blendSrc: THREE.OneFactor, blendDst: THREE.OneMinusSrcAlphaFactor
  });
  MATS = { photo, balloon, pu, bu };
  return MATS;
}

const _size = new THREE.Vector2();
const _c = new THREE.Color();
const _hsl = { h: 0, s: 0, l: 0 };

export class BalloonsVisualizer extends Visualizer {
  constructor(options) {
    super();
    const tier = options && options.qualityTier != null ? options.qualityTier : DEFAULT_TIER;
    this._tier = TIER[tier] ? tier : DEFAULT_TIER;
    this._count = TIER[this._tier].count;
    const bp = parseFloat(urlParam('balloonsp'));
    this._forcedP = Number.isFinite(bp) ? clamp01(bp) : null;
    const bb = urlParam('balloonsburn');
    this._debugBurn = bb === '1' ? 1 : (bb === '0' ? 0 : null);   // 1 all lit, 0 none (tests)
    this._debugDrop = urlParam('balloonsdrop') === '1';
    const pl = urlParam('balloonsplace');
    this._place = pl ? pl.split(',').map(Number) : null;    // x, y, size (screen units): one balloon, alone
  }

  // The photo's own horizon reaches 0.91 linear: at the usual 0.78 threshold
  // the bloom laid a white veil over half the frame and washed the balloons
  // near the sun. Only the flames and the lit lanterns cross 0.92.
  get postProcessing() { return { bloomStrength: 0.22, bloomThreshold: 0.92 }; }

  init(scene, camera) {
    this._scene = scene;
    this._assets = getBalloons();
    const m = materials();
    this._m = m;
    this._photo = new THREE.Mesh(new THREE.PlaneGeometry(2, 2), m.photo);
    this._photo.frustumCulled = false;
    this._photo.renderOrder = -1000;
    const geo = new THREE.InstancedBufferGeometry();
    geo.setAttribute('position', new THREE.Float32BufferAttribute([-1, -1, 0, 1, -1, 0, 1, 1, 0, -1, 1, 0], 3));
    geo.setIndex([0, 1, 2, 0, 2, 3]);
    const idx = new Float32Array(MAX_BALLOONS);
    for (let i = 0; i < MAX_BALLOONS; i++) idx[i] = i;
    geo.setAttribute('aIndex', new THREE.InstancedBufferAttribute(idx, 1));
    geo.instanceCount = this._place ? 1 : this._count;
    this._mesh = new THREE.Mesh(geo, m.balloon);
    this._mesh.frustumCulled = false;
    this._mesh.renderOrder = -900;
    const self = this;
    this._photo.onBeforeRender = function (renderer) { self._layout(renderer); };
    // Without the photo it is unavailable (the entry points withdraw it);
    // forced anyway, nothing is drawn: a black frame, not a broken one.
    this._photo.visible = this._mesh.visible = !!this._assets;
    scene.add(this._photo);
    scene.add(this._mesh);
    if (this._assets) {
      m.pu.uPhoto.value = this._assets.photo;
      m.pu.uMask.value = this._assets.mask;
      m.bu.uMask.value = this._assets.mask;
    }
    m.bu.uNoOcc.value = urlParam('balloonsnoocc') === '1' ? 1 : 0;

    this._time = 0;
    this._progress = null;
    this._trackChangedAt = Date.now();
    this._lastTrack = undefined;
    this._beatSeconds = 60 / FREE_BPM;
    this._sb = 0;
    this._lastBeatPhase = null; this._lastBarPhase = null; this._lastPhrase = null; this._lastMode = null;
    this._beatCount = 0; this._barCount = 0; this._freeBeat = 0;
    this._lastDropTime = -1e9; this._debugDropAt = 4;
    this._aspect = 16 / 9;
    this._pairs = DEFAULT_PAIRS;
    this._lastPaletteRef = undefined;
    this._burnCursor = 0;
    this._cascade = null;
    this._zoneMax = 0;
    this._fitKey = '';
    this._balloons = [];
    if (this._assets) {
      this._fit(16 / 9);
      for (let i = 0; i < (this._place ? 1 : this._count); i++) this._balloons.push(this._newBalloon(i, true));
    }
    this._write();
  }

  // --- geometry helpers --------------------------------------------------------------

  _fit(aspect) {
    const sz = this._assets.manifest.size;
    this._aspect = aspect;
    const f = coverScale(aspect, sz[0] / sz[1]);
    this._m.pu.uFit.value.copy(f);
    // the sun, in screen units
    const sun = this._assets.manifest.sun;
    this._sunBase = this._toScreen(sun[0], sun[1]);
  }
  _toScreen(ix, iy) {
    const f = this._m.pu.uFit.value, a = this._aspect;
    const vx = (ix - 0.5) / f.x + 0.5, vy = ((1 - iy) - 0.5) / f.y + 0.5;
    return [(vx * 2 - 1) * a, vy * 2 - 1];
  }
  _screenYOfImageRow(iy) { return this._toScreen(0.5, iy)[1]; }

  // The forbidden rectangles, screen units: the cover's zone and the three UI
  // zones (R6), each grown by the balloon's half size.
  _zones(S) {
    // R19: the centre of composition's zone, not the cover's
    const a = this._aspect, c = safeZoneUniforms.uStageCenter.value, hs = safeZoneUniforms.uStageHalfSize.value;
    const hx = 0.45 * S + 0.02, hyTop = 0.62 * S + 0.02, hyBot = 0.62 * S + 0.02;
    return [
      { x0: c.x - hs.x - hx, x1: c.x + hs.x + hx, y0: c.y - hs.y - hyTop, y1: c.y + hs.y + hyBot },
      { x0: a - 0.44 * a - hx, x1: 9, y0: 1 - 0.24 - hyTop, y1: 9 },            // top right 22 % x 12 %
      { x0: -9, x1: -a + 0.16 * a + hx, y0: 1 - 0.24 - hyTop, y1: 9 },          // top left 8 % x 12 %
      { x0: a - 0.44 * a - hx, x1: 9, y0: -9, y1: -1 + 0.20 + hyBot }           // bottom right 22 % x 10 %
    ];
  }
  _inZone(b) {
    const zs = this._zones(b.S);
    for (let k = 0; k < zs.length; k++) { const z = zs[k]; if (b.x > z.x0 && b.x < z.x1 && b.y > z.y0 && b.y < z.y1) return k + 1; }
    return 0;
  }

  // A balloon: size by a power law (more small far ones), proximity from the
  // size, colours from the cover's pairs. Born behind the land (below the
  // first row nearer than itself) or, at the start, anywhere allowed.
  _newBalloon(i, scatter) {
    const a = this._aspect;
    const r = Math.random();
    const S = this._place ? this._place[2] : SIZE_MIN + (SIZE_MAX - SIZE_MIN) * Math.pow(r, 1.8);
    const prox = S * PROX_PER_SIZE;
    const b = { i, S, prox, x: 0, y: 0, vx: 0, steer: 0, burnAt: -1e9, flick: Math.random() * 10, pair: (i * 2 + Math.floor(Math.random() * 3)) % this._pairs.length, seed: Math.random(), boostAt: -1e9 };
    if (this._place) { b.x = this._place[0]; b.y = this._place[1]; b.prox = this._place[3] != null ? this._place[3] : prox; return b; }
    const rows = this._assets.manifest.rowProximity;
    let k = rows.findIndex((v) => v > prox + 0.02);
    const startY = k >= 0 ? this._screenYOfImageRow((k + 0.5) / rows.length) - 0.65 * S : -1 - 0.7 * S;
    for (let tries = 0; tries < 60; tries++) {
      b.x = rand(-a + 0.5 * S + 0.05, a - 0.5 * S - 0.05);
      b.y = scatter ? rand(startY, 0.85) : startY;
      // born out of the cover's band and the zones, whatever its height
      const zs = this._zones(S);
      const inBand = b.x > zs[0].x0 && b.x < zs[0].x1;
      if (!inBand && !this._inZone(b)) break;
    }
    return b;
  }

  _applyPalette(palette) {
    const v = palette && palette.vividColors && palette.vividColors.length >= 2 ? palette.vividColors : null;
    if (!v) { this._pairs = DEFAULT_PAIRS; return; }
    // moderate saturation (the brief): the cover's hues at 0.55 saturation,
    // lightness 0.24 (HSL in linear: 0.42 was a pastel that left the lantern
    // nothing to light)
    const pairs = [];
    const n = Math.min(5, v.length);
    for (let k = 0; k < n; k++) pairs.push([v[k], v[(k + 2) % n]]);
    this._pairs = pairs.map((p) => p.map((h) => { _c.set(h).getHSL(_hsl); return new THREE.Color().setHSL(_hsl.h, 0.55, 0.24).getHex(); }));
  }

  onTrackChange(profile) { this._applyPalette(profile && profile.artworkPalette); }

  // --- per frame -------------------------------------------------------------------

  _layout(renderer) {
    if (!this._assets) return;
    renderer.getDrawingBufferSize(_size);
    const aspect = _size.x / Math.max(1, _size.y);
    this._m.bu.uPxUnit.value = _size.y / 2;
    if (Math.abs(aspect - this._aspect) > 1e-3) { this._fit(aspect); for (const b of this._balloons) if (this._inZone(b)) Object.assign(b, this._newBalloon(b.i, true)); }
  }

  update(deltaTime, profile) {
    if (!this._assets) return;
    if (profile.artworkPalette !== this._lastPaletteRef) { this._lastPaletteRef = profile.artworkPalette; this._applyPalette(profile.artworkPalette); }
    // Pause: frozen, never black (R11).
    if (profile.playbackState !== 'PLAYING') return;
    const dt = Math.min(deltaTime, 0.05);
    this._time += dt;
    if (profile.trackId !== this._lastTrack) { this._lastTrack = profile.trackId; this._progress = null; this._trackChangedAt = Date.now(); }
    let target;
    if (this._forcedP != null) target = this._forcedP;
    else if (profile.duration > 0) target = clamp01(profile.progress || 0);
    else target = ((Date.now() - (profile.trackChangedAt || this._trackChangedAt)) / 1000 / RADIO_LOOP_SECONDS) % 1;
    if (this._progress == null || Math.abs(target - this._progress) > 0.5) this._progress = target;
    else this._progress += (target - this._progress) * (1 - Math.exp(-dt / PROGRESS_TAU));

    const bpmRaw = profile.trackFeatures && profile.trackFeatures.bpm;
    this._beatSeconds = 60 / (bpmRaw > 0 ? THREE.MathUtils.clamp(bpmRaw, 50, 200) : FREE_BPM);
    const beat = this._beatSeconds;
    this._sb += dt / beat;
    // --- edges ---
    let beatEdge = false, barEdge = false, phraseEdge = false;
    if (profile.beatPhase != null) {
      if (this._lastBeatPhase != null && profile.beatPhase < this._lastBeatPhase - 0.5) beatEdge = true;
      this._lastBeatPhase = profile.beatPhase;
    } else {
      this._freeBeat += dt / beat;
      if (this._freeBeat >= 1) { this._freeBeat -= 1; beatEdge = true; }
    }
    if (beatEdge) this._beatCount++;
    if (profile.barPhase != null) {
      if (this._lastBarPhase != null && profile.barPhase < this._lastBarPhase - 0.5) barEdge = true;
      this._lastBarPhase = profile.barPhase;
    } else if (beatEdge && this._beatCount % 4 === 0) barEdge = true;
    if (barEdge) this._barCount++;
    const phrase = profile.phraseIndex;
    if (phrase != null && this._lastPhrase != null && phrase !== this._lastPhrase) phraseEdge = true;
    if (phrase == null && barEdge && this._barCount % 4 === 0) phraseEdge = true;
    if (phrase != null) this._lastPhrase = phrase;
    const director = profile.director || {};
    let dropNow = false;
    const ev = director.nextEvent;
    if (director.hasReliableGrid && director.mode === 'drop' && this._lastMode !== 'drop') dropNow = true;
    if (ev && ev.type === 'drop' && ev.msUntil != null && ev.msUntil <= 0) dropNow = true;
    this._lastMode = director.mode;
    if (this._debugDrop && this._time >= this._debugDropAt) { dropNow = true; this._debugDropAt = this._time + 24 * beat; }

    // --- the burners (lounge: on the bar, never a flash) ---
    if (dropNow && this._time - this._lastDropTime > 8 * beat) {
      this._lastDropTime = this._time;
      this._cascade = { at: this._sb, order: this._balloons.map((b) => b.i).sort(() => Math.random() - 0.5), next: 0 };
    }
    if (this._cascade) {
      const c = this._cascade, n = c.order.length;
      while (c.next < n && this._sb - c.at >= c.next * (8 / n)) { this._fire(c.order[c.next]); c.next++; }
      if (c.next >= n) this._cascade = null;
    } else if (barEdge) {
      this._fire(this._pickBurner());
      if (phraseEdge) this._fire(this._pickBurner());
    }
    this._physics(dt);
    this._write();
  }

  // the next burner: a different balloon each time, on screen and not hidden
  _pickBurner() {
    const n = this._balloons.length;
    for (let k = 0; k < n; k++) {
      this._burnCursor = (this._burnCursor + 1) % n;
      const b = this._balloons[this._burnCursor];
      if (b.y > -0.95 && b.y < 0.95 && this._sb - b.burnAt > 6) return b.i;
    }
    return this._balloons[this._burnCursor].i;
  }
  _fire(i) { const b = this._balloons.find((x) => x.i === i); if (b) { b.burnAt = this._sb; b.boostAt = this._sb; } }

  _burnLevel(b) {
    if (this._debugBurn != null) return this._debugBurn;
    const t = this._sb - b.burnAt;
    if (t < 0) return 0;
    if (t < BURN_ATTACK) return smooth01(t / BURN_ATTACK);
    if (t < BURN_HOLD) return 1;
    return 1 - smooth01((t - BURN_HOLD) / BURN_DECAY);
  }

  // Climb, drift, steer around the cover's zone and the UI zones, respawn.
  _physics(dt) {
    if (this._place) return;
    const a = this._aspect;
    const windT = Math.sin(this._time * 0.021) * 0.7 + Math.sin(this._time * 0.0083 + 1.3) * 0.3;
    for (const b of this._balloons) {
      const k = b.S / SIZE_MAX;
      const boost = 1 + BOOST * Math.exp(-Math.max(0, this._sb - b.boostAt) / BOOST_BEATS) * (this._sb >= b.boostAt ? 1 : 0);
      const vy = NEAR_SPEED * k * boost;
      let vx = WIND * k * (windT + (b.seed - 0.5) * 0.6);
      // steering: a zone above, within the time it takes to slide out of its
      // way, pushes the balloon sideways to its nearer free side. The look-
      // ahead is that TIME times the climb: a slow far balloon under a wide
      // zone starts early (a fixed 6 s look-ahead let one sit 43 s in the
      // top-right zone).
      let push = 0;
      const margin = 0.03;
      for (const z of this._zones(b.S)) {
        if (!(b.x > z.x0 - margin && b.x < z.x1 + margin)) continue;
        const toLeft = b.x - (z.x0 - margin), toRight = (z.x1 + margin) - b.x;
        const leftOk = z.x0 - margin > -a + 0.5 * b.S, rightOk = z.x1 + margin < a - 0.5 * b.S;
        let dir = toLeft < toRight ? -1 : 1;
        if (dir < 0 && !leftOk) dir = 1; else if (dir > 0 && !rightOk) dir = -1;
        const escape = dir < 0 ? toLeft : toRight;
        const ahead = vy * (escape / STEER_SPEED) * 1.4 + 0.03;
        if (b.y + ahead > z.y0 && b.y < z.y1) push += dir;
      }
      if (push !== 0) vx = Math.sign(push) * STEER_SPEED;
      b.x += vx * dt;
      b.y += vy * dt;
      b.x = Math.max(-a + 0.5 * b.S, Math.min(a - 0.5 * b.S, b.x));
      // zone bookkeeping (criterion 4): the longest continuous stay
      const zk = this._inZone(b);
      if (zk) { b.zoneT = (b.zoneT || 0) + dt; if (b.zoneT > this._zoneMax) { this._zoneMax = b.zoneT; this._zoneWho = { zone: zk, S: +b.S.toFixed(3), x: +b.x.toFixed(3), y: +b.y.toFixed(3) }; } } else b.zoneT = 0;
      if (b.y - 0.65 * b.S > 1.02) Object.assign(b, this._newBalloon(b.i, false));
    }
  }

  _write() {
    const bu = this._m.bu, pu = this._m.pu;
    const p = this._progress == null ? (this._forcedP != null ? this._forcedP : 0) : this._progress;
    pu.uP.value = p;
    if (!this._assets) return;
    // the sun climbs from just under the horizon to a little above it
    const sun = [this._sunBase[0], this._sunBase[1] - 0.04 + 0.12 * p];
    pu.uSun.value.set(sun[0], sun[1]);
    bu.uSunCol.value.setRGB(1.0, 0.62 + 0.2 * p, 0.38 + 0.25 * p);
    bu.uSunPow.value = 0.25 + 0.85 * p;
    bu.uAmbient.value.setRGB(0.10 + 0.08 * p, 0.12 + 0.08 * p, 0.20 + 0.04 * p);
    bu.uHaze.value.setRGB(0.16 + 0.12 * p, 0.18 + 0.10 * p, 0.27 + 0.03 * p);
    bu.uGlowGain.value = GLOW_DAWN + (GLOW_MORNING - GLOW_DAWN) * p;
    // far to near: the uniform slots in order of proximity
    const order = this._balloons.slice().sort((x, y) => x.prox - y.prox);
    for (let s = 0; s < MAX_BALLOONS; s++) {
      const b = order[s];
      if (!b) { bu.uB.value[s].set(0, -9, 0.0001, 0); bu.uC.value[s].set(0, 0, 0, 0); continue; }
      bu.uB.value[s].set(b.x, b.y, b.S, b.prox);
      const burn = this._burnLevel(b);
      const flick = 0.85 + 0.15 * Math.sin(this._time * 23 + b.flick) * Math.sin(this._time * 13 + b.flick * 2);
      const dSun = Math.hypot(b.x - sun[0], b.y - sun[1]);
      const backlight = (1 - smooth01((dSun - 0.15) / 0.75)) * 0.75 * (1 - 0.4 * p);
      const haze = (1 - clamp01((b.prox - 0.03) / 0.19)) * 0.30;
      bu.uC.value[s].set(burn, flick, backlight, haze);
      const pr = this._pairs[b.pair % this._pairs.length];
      bu.uColA.value[s].set(pr[0]); bu.uColB.value[s].set(pr[1]);
    }
  }

  // Test hook: the physics alone, over a long stretch (no render).
  simulate(seconds, dtS) {
    const dt = dtS || 1 / 30;
    let lastBar = Math.floor(this._sb / 4);
    for (let t = 0; t < seconds; t += dt) {
      this._time += dt; this._sb += dt / this._beatSeconds;
      // the burners' boosts too, one a bar, two on a phrase, as in update()
      const bar = Math.floor(this._sb / 4);
      if (bar !== lastBar) { lastBar = bar; this._fire(this._pickBurner()); if (bar % 4 === 0) this._fire(this._pickBurner()); }
      this._physics(dt);
    }
    return { zoneMax: this._zoneMax, who: this._zoneWho };
  }

  get probe() {
    return {
      ready: !!this._assets, progress: this._progress, count: this._balloons.length, zoneMax: this._zoneMax,
      balloons: this._balloons.map((b) => ({ i: b.i, x: +b.x.toFixed(3), y: +b.y.toFixed(3), S: +b.S.toFixed(3), prox: +b.prox.toFixed(3), burn: +this._burnLevel(b).toFixed(2) })),
      sun: this._m.pu.uSun.value.toArray(), fit: this._m.pu.uFit.value.toArray()
    };
  }

  dispose() {
    this._photo.onBeforeRender = null;
    this._scene.remove(this._photo);
    this._scene.remove(this._mesh);
    this._photo.geometry.dispose();
    this._mesh.geometry.dispose();
    this._m.pu.uPhoto.value = null; this._m.pu.uMask.value = null; this._m.bu.uMask.value = null;
    this._scene = null;
  }
}
